/**
 * PublicLogin.tsx — centralne logowanie multi-tenant (ADR-0075).
 *
 * Host: `app.<base>` (public scope). Zastępuje dawny email-only lookup: user loguje się
 * RAZ emailem + hasłem (+ ewentualnie kodem 2FA) przez `POST /api/auth/central-login`.
 *
 * Maszyna stanów jednego ekranu (parytet z ite_workflow365 Login.tsx):
 *  - 'credentials' — email + hasło.
 *  - '2fa'         — kod TOTP (gdy backend zwróci require2fa).
 *  - 'picker'      — "Wybierz konto": lista firm (>1) z plakietką "Administrator".
 *
 * Wejście do firmy (1 firma lub po wyborze): redirect na `<slug>.app.<base>/#__handoffKey`
 * (buildTenantHandoffUrl) — `main.tsx` (pickupHandoffKey) wymienia klucz na sesję.
 */

import { type FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronRight, ShieldCheck } from 'lucide-react';

import loginBg from '@/assets/wf-login-bg.png';
import workflowLogo from '@/assets/workflow365-logo.svg';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Input } from '@/components/Input';
import { AreaShowcase } from '@/components/login/AreaShowcase';
import { api } from '@/lib/api';
import { buildTenantHandoffUrl } from '@/lib/tenant-url';

interface TenantEntry {
  tenantId: string;
  slug: string;
  name: string;
  role: string;
  isAdmin: boolean;
  /** ADR-0113b §5: baza firmy w trakcie aktualizacji — na liście nieaktywna z dopiskiem. */
  updating?: boolean;
}

interface CentralLoginResponse {
  require2fa?: boolean;
  requiresTenantSelection?: boolean;
  tenant?: { tenantId: string; slug: string; name: string };
  tenants?: TenantEntry[];
  handoffKey?: string;
  selectionToken?: string;
  mustChangePassword?: boolean;
}

type Step = 'credentials' | '2fa' | 'picker';

function extractStatus(err: unknown): number | null {
  return (err as { response?: { status?: number } }).response?.status ?? null;
}

function extractErrorCode(err: unknown): string | null {
  return (err as { response?: { data?: { error?: string } } }).response?.data?.error ?? null;
}

export function PublicLogin() {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [tenants, setTenants] = useState<TenantEntry[]>([]);
  const [selectionToken, setSelectionToken] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(() => {
    // Redirect z main.tsx po nieudanej wymianie klucza handoff/impersonacji (redeem padł)
    // dokłada `?reason=handoff-failed` — pokaż notkę zamiast pustego ekranu logowania
    // (bug: pierwsze logowanie odbijało po cichu; ADR-0075).
    const reason = new URLSearchParams(window.location.search).get('reason');
    return reason === 'handoff-failed' ? t('public.login.errors.handoffFailed') : null;
  });
  const [mustChange, setMustChange] = useState(false);

  // Zdejmij `reason` z URL po odczytaniu (notka jest już w stanie) — inaczej parametr
  // „przykleja się" do adresu i ręczny reload `/login?reason=handoff-failed` pokazywałby
  // komunikat mimo że w tym przebiegu nic nie padło.
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get('reason') === 'handoff-failed') {
      url.searchParams.delete('reason');
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    }
  }, []);

  function toHandoff(tenant: { tenantId: string; slug: string }, handoffKey: string): void {
    window.location.href = buildTenantHandoffUrl({
      slug: tenant.slug,
      tenantId: tenant.tenantId,
      handoffKey,
    });
  }

  function handleError(err: unknown): void {
    const status = extractStatus(err);
    const code = extractErrorCode(err);
    // Zły kod 2FA (backend zwraca INVALID_2FA / 401) ma inny komunikat niż złe hasło —
    // na kroku 2FA email i hasło były już poprawne, „zły email lub hasło" myliłoby.
    // ADR-0113b §5: baza firmy w trakcie aktualizacji (503) — nie „brak dostępu”.
    if (code === 'TENANT_UPDATING') setError(t('tenantUpdating.loginError'));
    else if (code === 'INVALID_2FA') setError(t('public.login.errors.invalid2fa'));
    else if (status === 401) setError(t('public.login.errors.invalidCredentials'));
    else if (status === 403) setError(t('public.login.errors.noAccess'));
    else if (status === 429 || status === 423) setError(t('public.login.errors.rateLimited'));
    else setError(t('public.login.errors.generic'));
  }

  async function submitCredentials(withTotp: boolean): Promise<void> {
    setError(null);
    setSubmitting(true);
    try {
      const { data } = await api.post<CentralLoginResponse>('/auth/central-login', {
        email,
        password,
        ...(withTotp && totpCode ? { totpCode } : {}),
      });

      if (data.require2fa) {
        setStep('2fa');
        return;
      }
      setMustChange(!!data.mustChangePassword);

      if (data.requiresTenantSelection && data.tenants && data.selectionToken) {
        setTenants(data.tenants);
        setSelectionToken(data.selectionToken);
        setStep('picker');
        return;
      }
      if (data.tenant && data.handoffKey) {
        toHandoff(data.tenant, data.handoffKey);
        return;
      }
      setError(t('public.login.errors.generic'));
    } catch (err) {
      handleError(err);
    } finally {
      setSubmitting(false);
    }
  }

  async function onSelectTenant(tenantId: string): Promise<void> {
    setError(null);
    setSubmitting(true);
    try {
      const { data } = await api.post<CentralLoginResponse>('/auth/select-tenant', {
        selectionToken,
        tenantId,
      });
      if (data.tenant && data.handoffKey) {
        toHandoff(data.tenant, data.handoffKey);
        return;
      }
      setError(t('public.login.errors.generic'));
    } catch (err) {
      const status = extractStatus(err);
      // Token wyboru wygasł (10 min) → wróć do logowania.
      if (status === 401) {
        setStep('credentials');
        setError(t('public.login.errors.selectionExpired'));
      } else {
        handleError(err);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="flex min-h-screen bg-bg-secondary">
      {/* LEWY panel — formularz na miękkim gradiencie z Figmy (wf-login-bg, opacity 62%). */}
      <div className="relative flex flex-1 items-center justify-center overflow-hidden p-4 lg:w-1/2 lg:flex-none">
        <div
          className="pointer-events-none absolute inset-0 bg-cover bg-center opacity-[0.62]"
          style={{ backgroundImage: `url(${loginBg})` }}
          aria-hidden="true"
        />
        <div className="relative z-10 w-full max-w-md rounded-2xl border border-border-secondary bg-bg-elevated p-8 shadow-xl">
          <div className="mb-6 flex justify-center">
            <img src={workflowLogo} alt="Workflow365" className="h-9 w-auto" />
          </div>
          {step === 'picker' ? (
            <>
              <div className="mb-6 text-center">
                <h1 className="text-2xl font-bold text-text-primary">{t('public.picker.title')}</h1>
                <p className="mt-2 text-sm text-text-tertiary">
                  {t('public.picker.subtitle', { email })}
                </p>
              </div>
              {mustChange && (
                <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                  {t('public.login.mustChangePassword')}
                </div>
              )}
              {error && (
                <div
                  role="alert"
                  className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
                >
                  {error}
                </div>
              )}
              <ul className="space-y-3">
                {tenants.map((tenant) => (
                  <li key={tenant.tenantId}>
                    <Card
                      padding="none"
                      className={
                        tenant.updating
                          ? 'cursor-not-allowed opacity-60'
                          : 'cursor-pointer transition hover:border-brand-500 hover:shadow-md'
                      }
                      role="button"
                      tabIndex={tenant.updating ? -1 : 0}
                      aria-disabled={tenant.updating || undefined}
                      onClick={() =>
                        !submitting && !tenant.updating && onSelectTenant(tenant.tenantId)
                      }
                      onKeyDown={(e) => {
                        if (
                          (e.key === 'Enter' || e.key === ' ') &&
                          !submitting &&
                          !tenant.updating
                        ) {
                          e.preventDefault();
                          onSelectTenant(tenant.tenantId);
                        }
                      }}
                      aria-label={t('public.picker.chooseAria', { name: tenant.name })}
                      data-testid={`picker-tenant-${tenant.slug}`}
                    >
                      <div className="flex items-center justify-between p-4">
                        <div className="min-w-0">
                          <div className="truncate font-semibold text-text-primary">
                            {tenant.name}
                          </div>
                          {tenant.isAdmin && (
                            <div className="mt-1">
                              <Badge color="green">
                                <span className="flex items-center gap-1">
                                  <ShieldCheck size={12} aria-hidden="true" />
                                  {t('public.picker.adminBadge')}
                                </span>
                              </Badge>
                            </div>
                          )}
                          {tenant.updating && (
                            <div className="mt-1">
                              <Badge color="amber">{t('tenantUpdating.companyBadge')}</Badge>
                            </div>
                          )}
                        </div>
                        <ChevronRight size={18} className="text-text-tertiary" aria-hidden="true" />
                      </div>
                    </Card>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => {
                  setStep('credentials');
                  setError(null);
                }}
                className="mt-6 block w-full text-center text-sm text-text-tertiary hover:text-text-primary"
              >
                {t('public.picker.back')}
              </button>
            </>
          ) : (
            <>
              <div className="mb-8 text-center">
                <h1 className="text-2xl font-bold text-text-primary">
                  {step === '2fa' ? t('public.login.twoFaTitle') : t('public.login.title')}
                </h1>
                <p className="mt-2 text-sm text-text-tertiary">
                  {step === '2fa' ? t('public.login.twoFaSubtitle') : t('public.login.subtitle')}
                </p>
              </div>

              {error && (
                <div
                  role="alert"
                  className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
                >
                  {error}
                </div>
              )}

              {step === '2fa' ? (
                <form
                  onSubmit={(e: FormEvent) => {
                    e.preventDefault();
                    void submitCredentials(true);
                  }}
                  className="space-y-4"
                  noValidate
                >
                  <Input
                    label={t('public.login.twoFaLabel')}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                    required
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder={t('public.login.twoFaPlaceholder')}
                    disabled={submitting}
                    data-testid="public-login-2fa"
                  />
                  <Button type="submit" className="w-full" loading={submitting}>
                    {t('public.login.twoFaSubmit')}
                  </Button>
                  <button
                    type="button"
                    onClick={() => {
                      setStep('credentials');
                      setTotpCode('');
                      setError(null);
                    }}
                    className="block w-full text-center text-sm text-text-tertiary hover:text-text-primary"
                  >
                    {t('public.login.back')}
                  </button>
                </form>
              ) : (
                <form
                  onSubmit={(e: FormEvent) => {
                    e.preventDefault();
                    void submitCredentials(false);
                  }}
                  className="space-y-4"
                  noValidate
                >
                  <Input
                    label={t('public.login.emailLabel')}
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('public.login.emailPlaceholder')}
                    disabled={submitting}
                    data-testid="public-login-email"
                  />
                  <Input
                    label={t('public.login.passwordLabel')}
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={submitting}
                    data-testid="public-login-password"
                  />
                  <Button type="submit" className="w-full" loading={submitting}>
                    {submitting ? t('public.login.submitting') : t('public.login.submit')}
                  </Button>
                  <div className="flex items-center justify-center pt-2 text-xs">
                    <Link
                      to="/forgot-password"
                      className="text-text-tertiary underline underline-offset-2 hover:text-text-primary"
                      data-testid="public-login-forgot-link"
                    >
                      {t('public.login.forgot')}
                    </Link>
                  </div>
                  {/* Statyczna informacja o polityce blokady (logika po stronie backendu). */}
                  <p
                    className="pt-1 text-center text-xs text-text-tertiary"
                    data-testid="public-login-lockout-notice"
                  >
                    {t('public.login.lockoutNotice')}
                  </p>
                </form>
              )}
            </>
          )}
        </div>
      </div>
      {/* PRAWY panel — biała siatka obszarów produktu (parytet z Figmą). */}
      <AreaShowcase className="hidden lg:flex lg:w-1/2" />
    </main>
  );
}
